import { useEffect, useMemo, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { UserCombobox } from "@/features/users/components/UserCombobox";
import { PermissionScopeSection } from "./PermissionScopeSection";
import { PermissionPanelShell } from "./PermissionPanelShell";
import { PublishPermissionConfirmDialog } from "./PublishPermissionConfirmDialog";
import {
  usePermissionMatrix,
  useUserPermissions,
} from "@/features/permissions/hooks/usePermissionsQueries";
import { useUpdateUserPermissions } from "@/features/permissions/hooks/usePermissionsMutations";
import { requiresConfirmOnEnable } from "@/features/permissions/constants/permission-confirmations";
import { getRolePermissionsForPanel } from "@/features/permissions/utils/role-permissions";
import { getRoleLabel } from "@/features/roles/constants/role-labels";
import { useRoleLabelMap } from "@/features/roles/hooks/useRolesQueries";
import { useUser } from "@/features/users/hooks/useUsersQueries";
import { useUserPermissions as useMyAccess } from "@/hooks/useUserPermissions";
/** null = follow the role default (inherit). */
type OverrideChoice = boolean | null;

function userDisplayName(user: {
  firstName?: string;
  lastName?: string;
  email: string;
}) {
  return `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;
}

export function UserPermissionPanel({
  initialUserId,
}: {
  initialUserId?: string;
}) {
  const [selectedUserId, setSelectedUserId] = useState(initialUserId ?? "");
  const [pickedRole, setPickedRole] = useState("");
  const [overrides, setOverrides] = useState<Record<string, OverrideChoice>>({});
  const [pendingPublishToggle, setPendingPublishToggle] = useState<
    (() => void) | null
  >(null);

  const { data: selectedUser, isLoading: userProfileLoading } = useUser(
    selectedUserId,
  );
  const {
    data: matrix,
    isLoading: matrixLoading,
    isError: matrixError,
  } = usePermissionMatrix();
  const {
    data: userPerms,
    isLoading: userPermsLoading,
    isError: userPermsError,
  } = useUserPermissions(selectedUserId, !!selectedUserId);
  const updateMutation = useUpdateUserPermissions(selectedUserId);
  const roleLabelMap = useRoleLabelMap();

  const targetRole = userPerms?.role ?? selectedUser?.role ?? pickedRole;
  const { canManagePermissions, isSuperAdmin } = useMyAccess();
  // A Sub-Admin (users:manage-editor-permissions only) defines content and document access;
  // support / dashboard / analytics keys are Super Admin's to grant.
  const rolePermissions = useMemo(
    () =>
      getRolePermissionsForPanel(targetRole).filter(
        (permission) =>
          canManagePermissions ||
          permission.startsWith("content:") ||
          permission.startsWith("documents:"),
      ),
    [targetRole, canManagePermissions],
  );

  const isSuperAdminTarget = targetRole === "SUPER_ADMIN";
  const isLoading =
    !!selectedUserId &&
    (userProfileLoading || matrixLoading || userPermsLoading);
  const loadFailed = matrixError || userPermsError;
  const ready = !!selectedUserId && !isLoading && !!targetRole;

  const targetLabel = selectedUser
    ? userDisplayName(selectedUser)
    : selectedUserId;

  useEffect(() => {
    setOverrides({});
    setPendingPublishToggle(null);
  }, [selectedUserId]);

  const roleDefaults = useMemo(
    () => new Set(targetRole ? (matrix?.roleEffective?.[targetRole] ?? []) : []),
    [matrix?.roleEffective, targetRole],
  );

  const savedOverride = (perm: string): boolean | undefined =>
    userPerms?.overrides.find(
      (override) =>
        override.permission === perm &&
        !override.departmentId &&
        !override.pageSlug,
    )?.granted;

  const overrideChoice = (perm: string): OverrideChoice => {
    const local = overrides[perm];
    if (local === true || local === false || local === null) return local;
    const saved = savedOverride(perm);
    if (saved === true) return true;
    if (saved === false) return false;
    return null;
  };

  const effectiveFor = (perm: string): boolean => {
    const choice = overrideChoice(perm);
    const roleDefault = roleDefaults.has(perm);
    if (choice === true) return true;
    if (choice === false) return false;
    return roleDefault;
  };

  const applyToggle = (perm: string, checked: boolean) => {
    const roleDefault = roleDefaults.has(perm);
    const next: OverrideChoice = checked === roleDefault ? null : checked;
    setOverrides((prev) => ({ ...prev, [perm]: next }));
  };

  const handleToggle = (perm: string, checked: boolean) => {
    if (requiresConfirmOnEnable(perm, checked, effectiveFor(perm))) {
      setPendingPublishToggle(() => () => applyToggle(perm, checked));
      return;
    }
    applyToggle(perm, checked);
  };

  const desiredEntry = (
    perm: string,
  ): { granted: boolean; inherit?: boolean } | undefined => {
    const choice = overrideChoice(perm);
    const saved = savedOverride(perm);
    if (choice === null) {
      if (saved === undefined) return undefined;
      return { granted: false, inherit: true };
    }
    if (saved === choice) return undefined;
    return { granted: choice };
  };

  const changedEntries = useMemo(() => {
    const entries: Array<{
      permission: string;
      granted: boolean;
      inherit?: boolean;
    }> = [];

    for (const permission of rolePermissions) {
      const desired = desiredEntry(permission);
      if (desired) {
        entries.push({
          permission,
          granted: desired.granted,
          ...(desired.inherit ? { inherit: true } : {}),
        });
      }
    }

    return entries;
  }, [rolePermissions, overrides, userPerms, roleDefaults]);

  const handleSave = async () => {
    await updateMutation.mutateAsync({ entries: changedEntries });
    setOverrides({});
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-right text-sm font-medium text-foreground">
          اختر المستخدم
        </p>
        <p className="text-right text-xs text-muted-foreground">
          عدّل صلاحيات هذا المستخدم فوق قالب دوره.
        </p>
        <div className="max-w-md">
          <UserCombobox
            value={selectedUserId}
            listQuery={
              isSuperAdmin
                ? undefined
                : { role: "EDITOR", limit: 100 }
            }
            onChange={(userId, role) => {
              setSelectedUserId(userId);
              setPickedRole(role ?? "");
            }}
          />
        </div>
        {selectedUserId && targetRole && (
          <p className="text-right text-xs text-muted-foreground">
            الدور: {getRoleLabel(targetRole, roleLabelMap)}
          </p>
        )}
      </div>

      {!selectedUserId && (
        <p className="text-right text-sm text-muted-foreground">
          اختر مستخدمًا لتخصيص صلاحياته فوق قالب الدور.
        </p>
      )}

      {selectedUserId && isLoading && (
        <div className="flex justify-center py-12">
          <Spinner className="size-6" />
        </div>
      )}

      {selectedUserId && !isLoading && loadFailed && !ready && (
        <p className="text-right text-sm text-destructive">
          تعذّر تحميل صلاحيات هذا المستخدم. تحقّق من الاتصال وحاول مرة أخرى.
        </p>
      )}

      {ready && loadFailed && (
        <p className="text-right text-xs text-amber-600">
          تعذّر تحميل التخصيصات المحفوظة — يُعرض قالب الدور فقط حتى يعود الاتصال.
        </p>
      )}

      {ready && (
        <>
          {isSuperAdminTarget ? (
            <div className="rounded-lg border bg-card p-4 text-right">
              <p className="text-sm text-muted-foreground">
                المدير العام يملك جميع الصلاحيات دائمًا، والتخصيص لا يؤثر عليه.
              </p>
            </div>
          ) : (
            <PermissionPanelShell
              dirtyCount={changedEntries.length}
              isPending={updateMutation.isPending}
              onSave={handleSave}
              saveLabel="حفظ تخصيص المستخدم"
            >
              {rolePermissions.length > 0 ? (
                <PermissionScopeSection
                  title="صلاحيات المستخدم"
                  description="تخصيص فوق قالب الدور — المحتوى، المستندات، الدعم"
                  permissions={rolePermissions}
                  activeCount={rolePermissions.filter(effectiveFor).length}
                  isActive={effectiveFor}
                  renderControl={(perm) => (
                    <Switch
                      checked={effectiveFor(perm)}
                      onCheckedChange={(checked) => handleToggle(perm, checked)}
                    />
                  )}
                />
              ) : (
                <p className="text-right text-sm text-muted-foreground">
                  لا توجد صلاحيات قابلة للتخصيص لهذا الدور.
                </p>
              )}
            </PermissionPanelShell>
          )}
        </>
      )}

      <PublishPermissionConfirmDialog
        open={pendingPublishToggle !== null}
        mode="user"
        targetLabel={targetLabel}
        onConfirm={() => {
          pendingPublishToggle?.();
          setPendingPublishToggle(null);
        }}
        onCancel={() => setPendingPublishToggle(null)}
      />
    </div>
  );
}
